import { Component } from '@angular/core';
import { Router } from '@angular/router';
import {
  PopoverController,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { PanAppState, PlatformService, UserState } from '@panamax/app-state';
import { ClaimTicketService } from '@shared/services/claim-ticket.service';
import { AppService } from '@shared/services/app.service';
import { PopoverHelperService } from '@shared/helpers/popover.helpers';
import { ErrorPopoverComponent } from '@shared/components/header/error-popover/error-popover.component';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { CustomerReportingModalService } from './services/customer-reporting-modal.service';
import { CustomerSelectionOption } from '@app/customer/models/customer-selection-option.model';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import {
  CUSTOMER_REPORTING_ACTIONS,
  ReportStoredProcedure,
  selectIsDetailDataLoading,
  selectIsInvoiceHistoryLoading,
  selectIsProductDetailLoading,
  selectCsvCustomerReportError,
  selectTopPurchasesCustomerReport,
  selectIsSpendByCategoryLoading,
  selectIsTopPurchasesLoading,
  CustomerReportingAnalyticsService,
} from '@usf/ngrx-customer-reporting';
import { CustomerReportingCsvService } from './services/customer-reporting-csv.service';
import {
  TopPurchase,
  TopPurchasesResponseList,
} from './models/top-purchases.model';
import { ProductService } from '@shared/services/product/product.service';
import { CustomerReportingToastService } from './services/customer-reporting-toast.service';
import { FEATURES } from '@shared/constants/splitio-features';
import { CustomerReportingFilterService } from './services/customer-reporting-filter.service';
import { purchase } from 'node_modules/@coveo/headless/dist/definitions/features/commerce/context/cart/cart-actions';

@Component({
  selector: 'app-customer-reporting',
  templateUrl: './customer-reporting.page.html',
  styleUrls: ['./customer-reporting.page.scss'],
})
export class CustomerReportingPage
  implements ViewWillEnter, ViewWillLeave, ViewDidEnter
{
  constructor(
    public platformService: PlatformService,
    private claimTicketService: ClaimTicketService,
    private appService: AppService,
    private popoverController: PopoverController,
    private popoverHelperService: PopoverHelperService,
    private translateService: TranslateService,
    private store: Store,
    private customerReportingModal: CustomerReportingModalService,
    private customerReportingCsvService: CustomerReportingCsvService,
    private toastService: CustomerReportingToastService,
    readonly panAppState: PanAppState,
    private productService: ProductService,
    private router: Router,
    private trackingService: CustomerReportingAnalyticsService,
    private filterService: CustomerReportingFilterService,
  ) {}

  destroyed$ = new Subject<void>();
  backgroundImageStyle = "url('/assets/images/business-analytics.webp')";
  overlayGradient =
    'linear-gradient(90deg, rgba(47,47,47,0.57) 0%, rgba(47,47,47,0.77) 8%, rgba(47,47,47,0) 50%, rgba(47,47,47,0.57) 91%, rgba(47,47,47,0.57) 100%)';
  fromDate$: Observable<number>;
  toDate$: Observable<number>;
  deliveryDateRange$: Observable<string>;
  numberSelectedFilters$: Observable<number>;
  selectedCustomers$: Observable<CustomerSelectionOption[]>;
  customerDivisionFilter$: Observable<any[]>;
  currentUser: UserState;
  isCategoryReportLoading$: Observable<boolean>;
  isTopPurchasesLoading$: Observable<boolean>;
  showCsvs$: Observable<boolean>;
  isCategoryReportEnabled$: Observable<boolean>;
  isPurchasesReportEnabled$: Observable<boolean>;
  isDetailReportEnabled$: Observable<boolean>;
  isProductUsageReportEnabled$: Observable<boolean>;
  isInvoiceHistoryReportEnabled$: Observable<boolean>;
  isAllFeaturesLoaded$: Observable<boolean>;
  modalFilterSub: Subscription;
  toastClickSubscription: Subscription;
  csvErrorSubscription: Subscription;
  cleanUp = 0;
  selectedSegment = 'Dollars';
  availableReports: {
    storedProcedure: ReportStoredProcedure;
    buttonLabel: string;
    featureFlagCsv: string;
    featureFlagTableView: string;
  }[] = [
    {
      storedProcedure: 'SP_QUERY_SALES_DETAIL_DATA',
      buttonLabel: 'Detail Data',
      featureFlagCsv:
        FEATURES.split_dynamic_business_analytics_detail_report.name,
      featureFlagTableView:
        FEATURES.split_dynamic_business_analytics_sales_table.name,
    },
    {
      storedProcedure: 'SP_QUERY_PRODUCT_DETAIL_DATA',
      buttonLabel: 'Product Usage',
      featureFlagCsv:
        FEATURES.split_dynamic_business_analytics_product_report.name,
      featureFlagTableView:
        FEATURES.split_dynamic_business_analytics_product_table.name,
    },
    {
      storedProcedure: 'SP_QUERY_INVOICE_HISTORY_DATA',
      buttonLabel: 'Invoice History',
      featureFlagCsv:
        FEATURES.split_dynamic_business_analytics_invoice_report.name,
      featureFlagTableView:
        FEATURES.split_dynamic_business_analytics_invoice_table.name,
    },
  ];

  filteredReports: {
    storedProcedure: ReportStoredProcedure;
    buttonLabel: string;
    featureFlagCsv: string;
    featureFlagTableView: string;
  }[] = [];

  topPurchases: TopPurchase[] = [];
  displayedTopPurchases: TopPurchase[] = [];
  showAllTopPurchasesMobile = false;
  topPurchasesSortBy: 'Dollars' | 'Cases' = 'Dollars';
  isMobile = false;
  showNoPurchasesMessage = false;
  csvFlagStyle = false;
  disabledState = {
    detailData: false,
    productDetailData: false,
    invoiceHistoryData: false,
  };
  loadingState = {
    detailData: false,
    productDetailData: false,
    invoiceHistoryData: false,
  };
  showBusinessSkeleton = true;

  // New Observables for Table View feature flags
  isDetailTableViewEnabled$: Observable<boolean>;
  isProductUsageTableViewEnabled$: Observable<boolean>;
  isInvoiceHistoryTableViewEnabled$: Observable<boolean>;
  isAnyTableViewActive$: Observable<boolean>;

  ionViewWillEnter() {
    this.trackingService.trackPageLoad('reporting');
    this.panAppState.setPageTitle('Business Analytics');
    this.showBusinessSkeleton = true;
    this.isMobile =
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.mobile;
    this.filterService.initializeFilters();
    this.fromDate$ = this.filterService.fromDate$;
    this.toDate$ = this.filterService.toDate$;
    this.deliveryDateRange$ = this.filterService.deliveryDateRange$;
    this.numberSelectedFilters$ = this.filterService.numberSelectedFilters$;
    this.selectedCustomers$ = this.filterService.selectedCustomers$;
    this.customerDivisionFilter$ = this.filterService.customerDivisionFilter$;

    this.store
      .select(selectTopPurchasesCustomerReport)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: TopPurchasesResponseList) => {
        this.showNoPurchasesMessage =
          !response || response['rows']?.length === 0;
        const topPurchases = response['rows']?.map(item => ({
          name: item['Product Description'],
          category: item.Brand,
          amount: Number(item.Dollars),
          weight: item.Pack,
          productID: item['Product Number'],
          cases: Number(item.Cases),
          img: '',
          sortBy: item['SortedBy'],
        }));

        // Load product data for all top purchases
        if (topPurchases?.length) {
          const productNumbers = topPurchases.map(p => Number(p.productID));
          this.productService.loadProducts(productNumbers);
        }

        this.topPurchases = topPurchases;
        this.updateDisplayedTopPurchases();
      });

    this.panAppState.user$
      .pipe(
        filter(
          user =>
            !!user && !!user.userId && !!user.firstName && !!user.lastName,
        ),
        take(1),
      )
      .subscribe(user => {
        this.currentUser = user;
        // Load initial filters result
        this.loadSpendByCategoryReport(this.selectedSegment);
        this.getTopPurchases(this.topPurchasesSortBy);
      });

    // Set up all loading state subscriptions
    this.setupLoadingStateSubscriptions();

    this.isCategoryReportLoading$ = this.store.select(
      selectIsSpendByCategoryLoading,
    );

    this.isTopPurchasesLoading$ = this.store.select(
      selectIsTopPurchasesLoading,
    );

    this.modalFilterSub = this.customerReportingModal.filtersSelected
      .pipe(takeUntil(this.destroyed$))
      .subscribe(event => {
        if (this.filterService.onFilterChange(event)) {
          this.onFilterChange();
        }
      });

    this.csvErrorSubscription = this.store
      .select(selectCsvCustomerReportError)
      .pipe(filter(error => !!error))
      .subscribe(error => {
        // If more error validations are needed, we can add them here
        if (error.error === 'NO_DATA') {
          let report = '';
          if (error.report === 'SP_QUERY_SALES_DETAIL_DATA') {
            report = 'Detail Data Report';
            this.disabledState = {
              ...this.disabledState,
              detailData: true,
            };
          } else if (error.report === 'SP_QUERY_PRODUCT_DETAIL_DATA') {
            report = 'Product Usage Report';
            this.disabledState = {
              ...this.disabledState,
              productDetailData: true,
            };
          } else if (error.report === 'SP_QUERY_INVOICE_HISTORY_DATA') {
            report = 'Invoice History Report';
            this.disabledState = {
              ...this.disabledState,
              invoiceHistoryData: true,
            };
          }
          this.toastService.show(report, 'nodataerror');
        } else if (error.error === 'API_ERROR') {
          let report = '';
          if (error.report === 'SP_QUERY_SALES_DETAIL_DATA') {
            report = 'Detail Data Report';
            this.disabledState = {
              ...this.disabledState,
              detailData: false,
            };
          } else if (error.report === 'SP_QUERY_PRODUCT_DETAIL_DATA') {
            report = 'Product Usage Report';
            this.disabledState = {
              ...this.disabledState,
              productDetailData: false,
            };
          } else if (error.report === 'SP_QUERY_INVOICE_HISTORY_DATA') {
            report = 'Invoice History Report';
            this.disabledState = {
              ...this.disabledState,
              invoiceHistoryData: false,
            };
          } else if (error.report === 'SP_QUERY_TOP_PURCHASES_DATA') {
            report = 'Top Purchases Report';
          } else if (error.report === 'SP_QUERY_SPEND_BY_CATEGORY_DATA') {
            report = 'Spend By Category Report';
          }
          this.toastService.show(report, 'apierror', 0, true, error.errorCode);
        }
        // TODO: ELSE statement for generic errors
      });

    this.toastClickSubscription = this.toastService.toastClick.subscribe(
      toast => {
        this.openModal();
      },
    );
  }

  ionViewDidEnter(): void {
    // Existing CSV report flag observables (assuming these are for CSV visibility)
    this.isDetailReportEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_detail_report.name,
    ]);
    this.isProductUsageReportEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_product_report.name,
    ]);
    this.isInvoiceHistoryReportEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_invoice_report.name,
    ]);

    // New Table View report flag observables
    this.isDetailTableViewEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_sales_table.name,
    ]);
    this.isProductUsageTableViewEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_product_table.name,
    ]);

    this.isInvoiceHistoryTableViewEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_invoice_table.name,
    ]);

    this.isAnyTableViewActive$ = combineLatest([
      this.isDetailTableViewEnabled$,
      this.isProductUsageTableViewEnabled$,
      this.isInvoiceHistoryTableViewEnabled$,
    ]).pipe(
      map(
        ([isDetailActive, isProductUsageActive, isInvoiceHistoryActive]) =>
          isDetailActive || isProductUsageActive || isInvoiceHistoryActive,
      ),
    );

    this.isCategoryReportEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_category_report.name,
    ]);

    this.isPurchasesReportEnabled$ = this.panAppState.feature$([
      FEATURES.split_dynamic_business_analytics_purchases_report.name,
    ]);

    // Show CSVs container as long as one of the reports is enabled
    this.showCsvs$ = combineLatest([
      this.isDetailReportEnabled$,
      this.isProductUsageReportEnabled$,
      this.isInvoiceHistoryReportEnabled$,
    ]).pipe(
      map(([detail, product, invoice]) => {
        if (!(detail && product && invoice)) {
          this.csvFlagStyle = true;
        }
        return detail || product || invoice;
      }),
    );

    // Set the isAllFeaturesLoaded$ after all features have been initialized
    this.isAllFeaturesLoaded$ = of(false).pipe(
      switchMap(() =>
        combineLatest([
          this.isDetailReportEnabled$,
          this.isProductUsageReportEnabled$,
          this.isInvoiceHistoryReportEnabled$,
          this.isCategoryReportEnabled$,
          this.isPurchasesReportEnabled$,
        ]),
      ),
      map(() => {
        // Use setTimeout to ensure the skeleton displays for at least a moment
        // This helps with a consistent experience across platforms
        setTimeout(() => {
          this.showBusinessSkeleton = false;
        }, 500);

        // Initialize filtered reports
        this.updateFilteredReports();
        return true;
      }),
    );
  }

  ionViewWillLeave(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.modalFilterSub) {
      this.modalFilterSub.unsubscribe();
    }
    if (this.toastClickSubscription) {
      this.toastClickSubscription.unsubscribe();
    }
    if (this.csvErrorSubscription) {
      this.csvErrorSubscription.unsubscribe();
    }
    this.toastService.clearToasts();
    this.store.dispatch(
      CUSTOMER_REPORTING_ACTIONS.resetCsvCustomerReportError(),
    );
    this.selectedSegment = 'Dollars';
    this.cleanUp++;
  }

  onSegmentChange(event) {
    this.selectedSegment = event.detail.value;
  }

  handleReportAction(report: {
    storedProcedure: ReportStoredProcedure;
    featureFlagTableView: string;
  }) {
    if (!this.currentUser) {
      return;
    }
    this.panAppState
      .feature$([report.featureFlagTableView])
      .pipe(take(1))
      .subscribe(isTableViewEnabledForThisReport => {
        if (isTableViewEnabledForThisReport) {
          this.navigateToTableView(report.storedProcedure);
        } else {
          // Fallback to CSV download logic
          this.performCsvDownload(report.storedProcedure);
        }
      });
  }

  navigateToTableView(storedProcedure: ReportStoredProcedure) {
    let routePath = '';
    switch (storedProcedure) {
      case 'SP_QUERY_SALES_DETAIL_DATA':
        routePath = 'detail-data';
        break;
      case 'SP_QUERY_PRODUCT_DETAIL_DATA':
        routePath = 'product-usage';
        break;
      case 'SP_QUERY_INVOICE_HISTORY_DATA':
        routePath = 'invoice-history';
        break;
    }
    if (routePath) {
      this.router.navigate([`/customer-reporting/${routePath}`]);
    }
  }

  performCsvDownload(storedProcedure: ReportStoredProcedure) {
    const caller = {
      userName: this.currentUser.userId,
      firstName: this.currentUser.firstName,
      lastName: this.currentUser.lastName,
    };

    this.customerDivisionFilter$.pipe(take(1)).subscribe(divisions => {
      const filters = {
        limit: 10,
        invoiceDate: {
          start: this.formatDate(this.filterService.fromDate),
          end: this.formatDate(this.filterService.toDate),
        },
        divisions: divisions,
        sortBy: 'Dollars',
      };

      this.customerReportingCsvService.downloadCsv(
        caller,
        storedProcedure,
        filters,
      );
    });
  }

  openModal(quickDate: boolean = false) {
    this.customerReportingModal.open(
      this.filterService.fromDate,
      this.filterService.toDate,
      this.filterService.selectedCustomers,
      quickDate,
    );
  }

  goToFullBusinessAnalytics() {
    this.navigateWithClaimTicket(environment.baUrl, 'i18n.moxe-links.ba');
  }

  getTopPurchases(sortBy: 'Dollars' | 'Cases'): void {
    this.resetTopPurchases();
    this.customerDivisionFilter$.pipe(take(1)).subscribe(divisions => {
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadJsonTopPurchasesCustomerReport({
          storedProcedure: 'SP_QUERY_TOP_PURCHASES_DATA',
          filters: {
            limit: 10,
            invoiceDate: {
              start: this.formatDate(this.filterService.fromDate),
              end: this.formatDate(this.filterService.toDate),
            },
            divisions: divisions,
            sortBy,
          },
        }),
      );
    });
  }

  onFilterChange() {
    this.disabledState = {
      detailData: false,
      productDetailData: false,
      invoiceHistoryData: false,
    };
    this.loadSpendByCategoryReport(this.selectedSegment);
    this.getTopPurchases(this.topPurchasesSortBy);
    this.showAllTopPurchasesMobile = false;
    this.updateDisplayedTopPurchases();
    this.toastService.clearToasts();
  }

  resetFilters(fromDestroy: boolean = false) {
    this.filterService.resetFilters();
    this.disabledState = {
      detailData: false,
      productDetailData: false,
      invoiceHistoryData: false,
    };
    this.toastService.clearToasts();

    if (!fromDestroy) {
      // TODO: We can save the initial filters result to avoid another call
      this.loadSpendByCategoryReport(this.selectedSegment);
      this.getTopPurchases(this.topPurchasesSortBy);
      this.showAllTopPurchasesMobile = false;
      this.updateDisplayedTopPurchases();
    }
  }

  formatDate(date: number): string {
    if (!date) return '';

    const dateString = date.toString();

    if (dateString.length === 8) {
      const year = dateString.slice(0, 4);
      const month = dateString.slice(4, 6);
      const day = dateString.slice(6, 8);

      return `${year}-${month}-${day}`;
    }

    // If date is invalid, return empty string instead of throwing error
    return '';
  }

  loadSpendByCategoryReport(sortBy: string = 'Dollars') {
    this.customerDivisionFilter$.pipe(take(1)).subscribe(divisions => {
      this.store.dispatch(
        CUSTOMER_REPORTING_ACTIONS.loadJsonSpendByCategoryCustomerReport({
          storedProcedure: 'SP_QUERY_SPEND_BY_CATEGORY_DATA',
          filters: {
            limit: 10,
            invoiceDate: {
              start: this.formatDate(this.filterService.fromDate),
              end: this.formatDate(this.filterService.toDate),
            },
            divisions: divisions,
            sortBy,
          },
        }),
      );
    });
  }

  navigateWithClaimTicket(url: string, destinationName: string) {
    this.claimTicketService.getClaimTicket()?.subscribe(
      res => {
        if (res?.claimTicketValue) {
          url = url + res?.claimTicketValue;
          this.appService.openUrlInBrowser(url);
        } else {
          this.popoverHelperService.presentPopover(
            this.popoverController,
            ErrorPopoverComponent,
            `Unable to take you to ${this.translateService.instant(
              destinationName,
            )} at this time. Please try again later.`,
          );
        }
      },
      () => {
        this.popoverHelperService.presentPopover(
          this.popoverController,
          ErrorPopoverComponent,
          `Unable to take you to ${this.translateService.instant(
            destinationName,
          )} at this time. Please try again later.`,
        );
      },
    );
  }

  // Method to handle the ion-segment for Top Purchases
  onTopPurchasesSegmentChange(event): void {
    this.topPurchasesSortBy = event.detail.value;
    this.updateDisplayedTopPurchases();
  }

  toggleShowAllTopPurchases(): void {
    this.showAllTopPurchasesMobile = !this.showAllTopPurchasesMobile;
    this.updateDisplayedTopPurchases();
  }

  private updateDisplayedTopPurchases(): void {
    let filterTopPurchases = this.topPurchases?.filter(
      purchase =>
        this.validatePurchase(purchase) &&
        purchase.sortBy === this.topPurchasesSortBy,
    );
    filterTopPurchases =
      this.isMobile && !this.showAllTopPurchasesMobile
        ? filterTopPurchases?.slice(0, 3)
        : filterTopPurchases?.slice(0, 10);

    this.displayedTopPurchases = filterTopPurchases;
  }

  private validatePurchase(purchase: TopPurchase): boolean {
    return (
      purchase.name &&
      purchase.weight &&
      purchase?.category?.toLowerCase() !== 'unknown'
    );
  }

  private resetTopPurchases(): void {
    this.displayedTopPurchases = [];
    this.topPurchases = [];
    this.showNoPurchasesMessage = false;
  }

  private setupLoadingStateSubscriptions(): void {
    const loadingSelectors = [
      { selector: selectIsDetailDataLoading, key: 'detailData' },
      { selector: selectIsProductDetailLoading, key: 'productDetailData' },
      { selector: selectIsInvoiceHistoryLoading, key: 'invoiceHistoryData' },
    ];

    loadingSelectors.forEach(({ selector, key }) => {
      this.store
        .select(selector)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(isLoading => {
          this.loadingState = {
            ...this.loadingState,
            [key]: isLoading,
          };
        });
    });
  }

  updateFilteredReports(): void {
    // Subscribe to each feature flag and update the filtered reports
    this.availableReports.forEach(report => {
      this.panAppState
        .feature$([report.featureFlagCsv])
        .pipe(takeUntil(this.destroyed$))
        .subscribe(isEnabled => {
          if (isEnabled) {
            if (
              !this.filteredReports.some(
                r => r.storedProcedure === report.storedProcedure,
              )
            ) {
              this.filteredReports.push(report);
            }
          } else {
            this.filteredReports = this.filteredReports.filter(
              r => r.storedProcedure !== report.storedProcedure,
            );
          }
        });
    });
  }

  // Helper method to get the correct table view status observable for a given report
  isTableViewEnabledForReport(report: {
    featureFlagTableView: string;
  }): Observable<boolean> {
    switch (report.featureFlagTableView) {
      case FEATURES.split_dynamic_business_analytics_sales_table.name:
        return this.isDetailTableViewEnabled$;
      case FEATURES.split_dynamic_business_analytics_product_table.name:
        return this.isProductUsageTableViewEnabled$;
      case FEATURES.split_dynamic_business_analytics_invoice_table.name:
        return this.isInvoiceHistoryTableViewEnabled$;
      default:
        return of(false); // Default to false if no matching flag found
    }
  }
}
